/**
 * @file 文档页页右侧锚点菜单
 * @author FengGuang(fengguang01@baidu.com)
 */
import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import throttle from 'lodash/throttle';

import {IAnchorMenuItem} from './types';
import innerText from '../../../../../../utils/inner-text';
import getTreeFromArray from '../../../../../../utils/get-tree-from-array';

// 遍历所有选项并将他们平铺成数组
const getMenuArray = (theMenuList: IAnchorMenuItem[]): IAnchorMenuItem[] => {
    let resMenuArray: IAnchorMenuItem[] = [];
    theMenuList.forEach(menuItem => {
        resMenuArray.push(menuItem);
        resMenuArray = resMenuArray.concat(getMenuArray(menuItem.children));
    });
    return resMenuArray;
};

const useRightMenuHooks = (
    content: Object,
    contentDomRef: React.RefObject<HTMLDivElement>
) => {
    const location = useLocation();
    // 右侧锚点的内容
    const [menuList, setMenuList] = useState<IAnchorMenuItem[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    // 缓存锚点dom，用于监听滚动位置时判断锚点是否在视野里
    const anchorDomListRef = useRef<HTMLElement[]>([]);

    useEffect(() => {
        if (contentDomRef.current && typeof (document) !== 'undefined') {
            // 为页面中的 h1、h2.。。。标签添加锚点
            const list = contentDomRef.current.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5');
            const anchorDomList: HTMLElement[] = [];
            const resGroup: IAnchorMenuItem[] = [];
            const allKey: string[] = [];

            list.forEach((item, index) => {
                const itemLevel = item.tagName.toLocaleLowerCase();
                const itemKey = item.id || `anchor-${index}`;
                item.removeAttribute('id');

                const anchorDom = document.createElement('a');
                anchorDom.id = itemKey;
                anchorDom.name = itemKey;
                anchorDom.className = 'hide-anchor';
                if (item.parentElement) {
                    item.parentElement.insertBefore(anchorDom, item);
                    anchorDomList.push(anchorDom);
                }

                allKey.push(itemKey);
                resGroup.push({
                    key: itemKey,
                    level: itemLevel,
                    link: `#${itemKey}`,
                    title: innerText(item),
                    children: []
                });
            });
            anchorDomListRef.current = anchorDomList;
            const resGroup2 = getTreeFromArray(
                resGroup,
                ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                (item, level) => item.level === level
            );
            const resGroup3 = resGroup2.length === 1 ? resGroup2[0].children : resGroup2;
            setMenuList(resGroup3);
            // setOpenKeys(allKey);

            return () => {
                anchorDomList.forEach((item) => {
                    item.parentElement?.removeChild(item);
                });
            };
        }
    }, [content, setMenuList, setOpenKeys, contentDomRef]);

    const locationHash = location.hash;
    useEffect(() => {
        setSelectedKeys([locationHash?.slice(1) ?? '']);
    }, [locationHash]);


    // 右侧锚点的索引，方便快速查找对应的锚点数据
    const menuMapByLink = useMemo(() => {
        const theMap = new Map<string, IAnchorMenuItem>();
        const menuArray = getMenuArray(menuList);
        menuArray.forEach(menuItem => {
            const key = (menuItem.link || '').replace(/[^#]*#/, '');
            theMap.set(key, menuItem);
        });
        return theMap;
    }, [menuList]);

    // 监听锚点dom的位置
    useEffect(() => {
        // 使用 throttle 和 requestAnimationFrame 减轻计算压力
        const scrollHandle = throttle(() => requestAnimationFrame(() => {
            const anchorDomList = anchorDomListRef.current;
            if (anchorDomList.length === 0) {
                return;
            }
            let targetanchorDom: HTMLElement | undefined;
            // top 位置在屏幕中线和顶部之间的锚点 dom 被认为是正在展示的锚点
            const screenCenterLine = window.innerHeight / 3;
            const offset = 10;

            // 获取锚点 dom 的 top 值
            const anchorDomTopList = anchorDomList.map(anchorDom => anchorDom.getBoundingClientRect().top + offset);
            // 找到最接近屏幕顶端的锚点
            const anchorDomBellowTop = anchorDomTopList.filter(top => top > 0);
            if (anchorDomBellowTop.length > 0) {
                const targetTop = anchorDomBellowTop[0];
                const targetIndex = anchorDomTopList.indexOf(targetTop);
                // 如果这个锚点在屏幕上半部分，就认为它是当前展示的锚点
                if (targetTop < screenCenterLine) {
                    targetanchorDom = anchorDomList[targetIndex];
                }
                // 如果这个锚点在屏幕下半部分则认为他之前的那个锚点是当前正在展示的
                else {
                    targetanchorDom = anchorDomList[targetIndex - 1] || anchorDomList[targetIndex];
                }
            }
            // 屏幕里没有正在展示的锚点，直接取列表末尾的
            else {
                targetanchorDom = anchorDomList.slice(-1)[0];
            }

            if (targetanchorDom) {
                const targetLink = targetanchorDom.getAttribute('name') || '';
                const targetMenuItem = menuMapByLink.get(targetLink);
                if (targetMenuItem) {
                    setSelectedKeys((old) => {
                        return old[0] === targetMenuItem.key
                            ? old
                            : [targetMenuItem.key];
                    });
                    const getPathKeys = (item: IAnchorMenuItem) => {
                        const parentKeys: string[] = [];
                        let current = item;
                        while (current.parent) {
                            parentKeys.push(current.key);
                            current = current.parent;
                        }
                        return parentKeys;
                    };
                    setOpenKeys(getPathKeys(targetMenuItem));
                }
            }
        }), 500);
        window.addEventListener('scroll', scrollHandle);
        window.addEventListener('resize', scrollHandle);
        return () => {
            window.removeEventListener('scroll', scrollHandle);
            window.removeEventListener('resize', scrollHandle);
        };
    }, [content, menuList, menuMapByLink, setSelectedKeys]);


    return {
        menuList,
        setMenuList,
        selectedKeys,
        setSelectedKeys,
        openKeys,
        onOpenChange: setOpenKeys
    };
};

export default useRightMenuHooks;
